import type { Post } from '@/@types/post'
import { MEDIA_META } from '@/constants/api'
import {
	type Attachment,
	AttachmentStatus,
	AttachmentType,
	FileAttachment,
} from '@/states/attachment'
import { VIDEO_UPLOAD_LIMIT_QUERY_KEY, VideoUploadLimit } from '@/utils/fetchers/manifest'
import { getVideoMetadata } from '@/utils/media'
import queryClient from '@/utils/queryClient'
import { compact } from 'lodash-es'
import { z } from 'zod'
import { type PostFormValue } from './schemas'

export function toPostFormValue(data: Post): PostFormValue {
	const videoURL = queryClient.getQueryData<string>([MEDIA_META, data.id])
	return {
		id: data.id,
		content: data.content,
		attachments: compact([
			videoURL &&
				({
					id: videoURL,
					type: AttachmentType.VIDEO,
					status: AttachmentStatus.UPLOADED,
					cdn: {
						file: {
							fileUrl: videoURL,
						},
					},
				} as FileAttachment),
			...((data.pictures?.map((item) => ({
				id: item.key,
				type: AttachmentType.PICTURE,
				status: AttachmentStatus.UPLOADED,
				cdn: {
					file: {
						fileUrl: item.picUrl,
						key: item.key,
					},
				},
			})) || []) as FileAttachment[]),
			data.linkInfo && {
				id: data.linkInfo.linkUrl,
				type: AttachmentType.LINK,
				status: AttachmentStatus.UPLOADED,
				linkInfo: data.linkInfo,
			},
		]) as Attachment[],
		topic: data.topic,
	}
}

// 视频校验函数
export async function validateVideo(attachment: FileAttachment, ctx: z.RefinementCtx) {
	const limit = queryClient.getQueryData<VideoUploadLimit>(VIDEO_UPLOAD_LIMIT_QUERY_KEY) ?? {
		maxDuration: 360,
		maxSize: 100,
	}
	const params = {
		type: 'ONLY_AVAILABLE_IN_JIKE_APP',
	}

	// 检查视频格式
	if (attachment.file && !attachment.file.name.toLowerCase().endsWith('.mp4')) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: '仅支持 MP4 格式的视频',
			params,
		})
	}

	// 检查视频大小
	if (attachment.file && attachment.file.size > limit.maxSize * 1024 * 1024) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: `视频大小不能超过 ${limit.maxSize}MB`,
			params,
		})
	}

	const metadata = await getVideoMetadata(attachment.file)
	// 检查视频分辨率
	if (metadata.width && metadata.height) {
		const minSide = Math.min(metadata.width, metadata.height)
		const maxSide = Math.max(metadata.width, metadata.height)

		if (minSide > 1080 || maxSide > 1920) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '视频分辨率不能超过 1920x1080',
				params,
			})
		}
	}

	// 检查视频时长
	if (metadata.duration) {
		if (metadata.duration < 3) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '视频时长不能少于 3 秒',
			})
		} else if (metadata.duration > limit.maxDuration) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: `视频时长不能超过 ${limit.maxDuration}秒`,
			})
		}
	}
}

export function validateVideoAttachmentsStatus(
	videoAttachments: FileAttachment[],
	ctx: z.RefinementCtx
) {
	switch (videoAttachments[0].status) {
		case AttachmentStatus.UPLOAD_FAILED:
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '视频上传失败，请重试',
			})
			break
		case AttachmentStatus.UPLOADING:
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '视频上传中，请稍候',
			})
			break
		case AttachmentStatus.WAIT_TO_UPLOAD:
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '请等待视频上传',
			})
			break
		default:
			break
	}
}

export function validatePictureAttachmentsStatus(
	pictureAttachments: FileAttachment[],
	ctx: z.RefinementCtx
) {
	pictureAttachments.forEach((item) => {
		if (item.status !== AttachmentStatus.UPLOADED) {
			switch (item.status) {
				case AttachmentStatus.UPLOAD_FAILED:
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: '图片上传失败，请重试',
					})
					break
				case AttachmentStatus.UPLOADING:
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: '图片上传中，请稍候',
					})
					break
				case AttachmentStatus.WAIT_TO_UPLOAD:
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: '请等待图片上传',
					})
					break
				default:
					break
			}
		}
	})
}
